import { h, Host } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { debounce } from "../../utils/debounce";
import { formatPrefix, getCallingCodeForCountry, getValidator } from "./phone-input-validator";
import { getCountryDataProvider, getDefaultLanguage } from "./regions";
import RegionItem from "./region-item";
import { dataValidation } from "../form/validation-attributes";
const baseClass = "ux-phone-input";
export class PhoneInput {
    constructor() {
        this.inputId = undefined;
        this.label = "";
        this.labelInfo = "";
        this.name = "";
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.errorMsg = undefined;
        this.value = "";
        this.language = "";
        this.country = "";
        this.countryOptions = undefined;
        this.isDropdownOpen = false;
        this.hasFocus = false;
        this.menuItems = [];
        this.activeMenuItem = -1;
    }
    /** DOM node */
    domNode;
    /** DOM node of the input element */
    inputElement = null;
    invalidMsg;
    requiredMsg;
    setLanguage() {
        this.loadCountries();
    }
    setCountry() {
        if (this.country.length > 1) {
            this.activeMenuItem = getOptionIndexForCountry(this.menuItems, this.country);
            const prefix = formatPrefix(getCallingCodeForCountry(this.country));
            // @ts-expect-error Check for typing
            if (this.value.substr(0, prefix.length) !== prefix) {
                this.value = prefix;
            }
        }
        if (this.initialCountry === null) {
            this.initialCountry = this.country;
        }
    }
    setInitialValue() {
        if (this.value !== "") {
            // @ts-expect-error Check for typing
            this.updateValueAndCountry(this.value);
        }
    }
    setActiveMenuItem() {
        if (this.menuItems[this.activeMenuItem] !== undefined) {
            this.updateDropdownMenu();
        }
    }
    handleDropdownOpen() {
        if (this.isDropdownOpen) {
            document.addEventListener("click", this.handleClickDoc);
        }
        else {
            document.removeEventListener("click", this.handleClickDoc);
        }
    }
    componentWillLoad() {
        if (this.inputId === undefined) {
            this.inputId = getUniqueId();
        }
        if (this.language === "") {
            this.language = getDefaultLanguage();
        }
        this.setLanguage();
        this.setInitialValue();
        this.setCountry();
        // @ts-expect-error Check for typing
        this.invalidMsg = this.domNode.getAttribute(dataValidation.invalid.msg);
        // @ts-expect-error Check for typing
        this.requiredMsg = this.domNode.getAttribute(dataValidation.required.msg);
    }
    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
    }
    // @ts-expect-error Check for typing
    initialCountry = null;
    loadCountries = async () => {
        try {
            const countries = await getCountryDataProvider(this.language);
            const options = this.countryOptions !== undefined ? this.countryOptions.split(/[^A-Z]/g).filter((str) => str.length === 2) : [];
            this.menuItems = countries.filter(({ country }) => options.length === 0 || options.includes(country));
            if (this.country === "CN") {
                this.menuItems = countries.filter(({ country }) => country !== "TW");
            }
            this.activeMenuItem = getOptionIndexForCountry(this.menuItems, this.country);
        }
        catch {
            this.menuItems = [];
            this.activeMenuItem = -1;
        }
    };
    updateValueAndCountry = (value, invalidate = false) => {
        const validator = getValidator(value, this.country);
        this.value = validator.getFormattedValue();
        if (validator.isValid()) {
            const country = validator.getCountry();
            if (country !== undefined && country.length > 0) {
                this.country = country;
            }
        }
        else if (this.required && invalidate) {
            this.error = true;
        }
    };
    updateDropdownMenu = debounce(() => {
        const container = this.domNode.querySelector(`.${baseClass}__dropdown`);
        if (container === null) {
            return;
        }
        if (this.activeMenuItem < 0) {
            return;
        }
        const item = container.querySelectorAll(".ux-phone-input__option")[this.activeMenuItem];
        if (item === undefined) {
            return;
        }
        const offsetTop = item.offsetTop;
        const offsetBottom = offsetTop + item.offsetHeight;
        const scrollTop = container.scrollTop;
        const scrollBottom = scrollTop + container.clientHeight;
        if (scrollTop > offsetTop) {
            container.scrollTop += offsetTop - scrollTop;
        }
        else if (scrollBottom < offsetBottom) {
            container.scrollTop += offsetBottom - scrollBottom;
        }
    }, 10);
    toggleDropdownOpen = () => {
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.inputElement !== null) {
            this.inputElement.focus();
        }
        if (this.isDropdownOpen) {
            this.updateDropdownMenu();
        }
    };
    handleClickDoc = (event) => {
        if (!this.isDropdownOpen) {
            return;
        }
        if (event.target.closest("ux-phone-input") === this.domNode) {
            return;
        }
        this.isDropdownOpen = false;
    };
    handleInputChange = (event) => {
        const input = event.target;
        const fixedValue = input.value.replace(/[^0-9+\-() ]/g, "");
        if (input.value !== fixedValue) {
            input.value = fixedValue;
        }
        this.error = false;
        if (event.type === "input") {
            this.isDropdownOpen = false;
        }
        this.updateValueAndCountry(input.value, true);
    };
    handleFocus = () => {
        // @ts-expect-error Check for typing
        this.errorMsg = null;
        this.hasFocus = true;
    };
    handleBlur = (event) => {
        this.hasFocus = false;
        this.handleInputChange(event);
    };
    handleSelectCountry = (country) => {
        this.country = country;
        this.isDropdownOpen = false;
        if (this.inputElement !== null) {
            this.inputElement.focus();
        }
    };
    handleKeyPress = (event) => {
        const { activeMenuItem, isDropdownOpen, menuItems } = this;
        const isInvalidChar = event.key.length === 1 && /[^0-9+\-() ]/i.test(event.key);
        switch (event.key) {
            case "Enter":
                if (isDropdownOpen) {
                    event.preventDefault();
                    if (menuItems[activeMenuItem] !== undefined) {
                        this.handleSelectCountry(menuItems[activeMenuItem].country);
                    }
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                if (menuItems.length === 0) {
                    return;
                }
                if (!isDropdownOpen) {
                    this.isDropdownOpen = true;
                }
                else {
                    this.activeMenuItem = Math.min(this.menuItems.length - 1, this.activeMenuItem + 1);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (menuItems.length === 0) {
                    return;
                }
                if (!isDropdownOpen) {
                    this.isDropdownOpen = true;
                }
                else {
                    this.activeMenuItem = Math.max(0, this.activeMenuItem - 1);
                }
                break;
            case "Escape":
                if (isDropdownOpen) {
                    event.preventDefault();
                    this.isDropdownOpen = false;
                }
                break;
            default:
                if (isInvalidChar && !event.metaKey) {
                    event.preventDefault();
                    if (isDropdownOpen) {
                        const char = event.key.toLowerCase();
                        const menuItem = this.menuItems.filter(({ label }) => label.substr(0, 1).toLowerCase() === char)[0];
                        if (menuItem !== undefined) {
                            this.activeMenuItem = this.menuItems.indexOf(menuItem);
                        }
                    }
                }
                break;
        }
    };
    render() {
        const { initialCountry, country, disabled, error, errorMsg, hasFocus, inputId, isDropdownOpen, label, labelInfo, menuItems, name, required, value, invalidMsg, requiredMsg } = this;
        const validator = getValidator(value, country);
        // @ts-expect-error Check for typing
        const hasNumber = value.trim().length > validator.getPrefix().length;
        const hasMenuItems = menuItems.length > 0;
        const isInvalid = !error && hasNumber ? !validator.isValid() : error;
        let errorMessage = hasNumber && isInvalid ? errorMsg || invalidMsg : errorMsg || requiredMsg;
        // @ts-expect-error Check for typing
        errorMessage = errorMessage === "" ? null : errorMessage;
        return (h(Host, { key: '975a0e2764eeb238fcea996a3d7c4bcf9d4636ea', class: {
                [baseClass]: true,
                [`${baseClass}--disabled`]: disabled,
                [`${baseClass}--required`]: required,
                [`${baseClass}--error`]: isInvalid,
                [`${baseClass}--active`]: hasFocus || isDropdownOpen,
                [`${baseClass}--dropdown-opened`]: isDropdownOpen
            } }, h("ux-input-label", { key: 'cbf85486f9163294cf03e9725f928380d7c5996e', text: label, inputId: inputId, error: isInvalid, errorMsg: errorMessage, required: required, labelInfo: labelInfo }, h("slot", { key: '292d0522d4395975f2c3078889496410c9e30981' })), h("div", { key: '1697aef71f4afd3ac376aa1944dd6952dc9a3c64', class: `${baseClass}__inner` }, h("input", { key: 'ef0af31b99ed7ae11e5dbecae0c2de44d004f966', autoComplete: "off", autoCorrect: "off", class: {
                [`ux-input`]: true,
                [`${baseClass}__input`]: true
            }, inputMode: "tel", disabled: disabled, id: inputId, onInput: this.handleInputChange, onFocus: this.handleFocus,
            // @ts-expect-error Check for typing
            onBlur: this.handleBlur, onKeyDown: this.handleKeyPress,
            // @ts-expect-error Check for typing
            ref: (el) => (this.inputElement = el), type: "tel", value: value, "aria-invalid": isInvalid ? "true" : "false" }), h("input", { key: '91ce98cd29fec8a82c0b89d16a1a92fbd0f2f23f', name: name, required: required, style: { display: "none" }, autoComplete: "off", autoCorrect: "off", "aria-hidden": "true", type: "text", disabled: disabled, value: validator.getValidatedValue() }), h("button", { key: '85c76aba6ed5f676e8c19b29948701ac1e7a7908', class: {
                [`${baseClass}__dropdown-button`]: true,
                [`${baseClass}__dropdown-button--disabled`]: disabled || !hasMenuItems
            }, onClick: hasMenuItems ? this.toggleDropdownOpen : undefined, type: "button" }, h("ux-country-icon", { key: '334634a907ceb4d554f4341c15825bf3b5178110', country: initialCountry === "CN" && country === "TW" ? "" : country }), h("ux-icon", { key: '489d0e9ed214326255fd8978206b292f9f14ebd3', name: "chevron-down", weight: "bold" }))), hasMenuItems && isDropdownOpen && (h("div", { key: 'c40af561d76a97f7f514aba77e200621928bd45e', class: `${baseClass}__dropdown` }, h("ul", { key: '27d39ddcaad678c88bb520ac718f7559307dfaeb', role: "listbox" }, this.menuItems.map((region, index) => (h(RegionItem, { active: index === this.activeMenuItem, baseClass: baseClass, onSelect: this.handleSelectCountry, region: region }))))))));
    }
    static get is() { return "ux-phone-input"; }
    static get originalStyleUrls() {
        return {
            "$": ["phone-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["phone-input.css"]
        };
    }
    static get properties() {
        return {
            "inputId": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Used for the input id"
                },
                "attribute": "input-id",
                "reflect": false
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Used to render an input label"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "labelInfo": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "additional info label for the input field"
                },
                "attribute": "label-info",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Used for the input name"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates that the field is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates that the field is mandatory"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates an invalid value. If set to true, an optional errorMsg is shown."
                },
                "attribute": "error",
                "reflect": true,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Error message text that is rendered in error state"
                },
                "attribute": "error-msg",
                "reflect": false
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "value for the input"
                },
                "attribute": "value",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Language in which country names should be displayed"
                },
                "attribute": "language",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "country": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Default or detected phone number country code (ISO-2)"
                },
                "attribute": "country",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "countryOptions": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "List of country codes to display in the country dropdown\n(comma-separated, uppercase, e.g. \"DE,FR,CN,IT,US\"). If unset or empty, all available countries are shown."
                },
                "attribute": "country-options",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "isDropdownOpen": {},
            "hasFocus": {},
            "menuItems": {},
            "activeMenuItem": {}
        };
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "setLanguage"
            }, {
                "propName": "countryOptions",
                "methodName": "setLanguage"
            }, {
                "propName": "country",
                "methodName": "setCountry"
            }, {
                "propName": "activeMenuItem",
                "methodName": "setActiveMenuItem"
            }, {
                "propName": "isDropdownOpen",
                "methodName": "handleDropdownOpen"
            }];
    }
}
const getOptionIndexForCountry = (options, country) => {
    let optionIndex = -1;
    if (options.length === 0 || country.length < 2) {
        return optionIndex;
    }
    options.forEach((option, index) => {
        if (optionIndex === -1 && option.country.valueOf() === country) {
            optionIndex = index;
        }
    });
    return optionIndex;
};
//# sourceMappingURL=phone-input.js.map
